import { useRef, useState, useCallback } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useT } from '../i18n/useT';
import { F } from '../styles/theme';
import { Icon, Icons } from './red/Icons';

// iOS-Drawer-Snap (kräftig, „echt").
const EASE_SNAP = 'cubic-bezier(0.32, 0.72, 0, 1)';
// Back-out (Overshoot) — gibt dem Icon das „Pop" (mehr Nachschwung = premium).
const POP = 'cubic-bezier(0.34, 1.62, 0.5, 1)';

/**
 * iOS-style swipe-to-reveal.
 *
 * Verhalten (bewusst): die Karte lässt sich NICHT nach außen rausschieben —
 * der Inhalt gleitet höchstens bis ACTION_W (+ gedämpftes Gummiband) und
 * schnappt beim Loslassen entweder offen oder zu. Das Action-Icon (Auge)
 * „poppt" beim Schieben mit Spring/Overshoot auf. Ausgelöst wird die Action
 * durch Tippen auf den freigelegten Streifen.
 *
 * Drag ist ref-basiert (kein React-State pro Move) → buttery 1:1, kein Lag.
 */
export default function SwipeAction({
  children,
  actionLabel,
  actionIcon,
  actionColor,
  onAction,
  radius = 16,
  direction = 'left',
  showLabel = false,
}) {
  const { T } = useTheme();
  const { t } = useT();
  const label = actionLabel ?? t('misc_hide');
  // Icon-Farbe (KEINE Fläche mehr): Verbergen = Koralle-Akzent (Default),
  // Zeigen = Grün (Aufrufer übergibt actionColor).
  const color = actionColor || '#E4544A';

  const ACTION_W = 76;               // Reveal-Breite
  const TRIGGER = ACTION_W * 0.5;     // ab hier schnappt es beim Loslassen auf
  const MAX = ACTION_W + 36;          // harte Obergrenze inkl. Gummiband → nie „raus"
  const FLICK = 0.5;                  // px/ms
  const sign = direction === 'left' ? -1 : 1;

  const [open, setOpen] = useState(false);

  const contentRef = useRef(null);
  const iconRef = useRef(null);
  const startX = useRef(0);
  const startY = useRef(0);
  const dxRef = useRef(0);
  const baseRef = useRef(0);
  const lastX = useRef(0);
  const lastT = useRef(0);
  const vel = useRef(0);
  const tracking = useRef(false);
  const moved = useRef(false);
  const tick = useRef(false);

  const haptic = (ms = 6) => { try { navigator.vibrate?.(ms); } catch {} };

  // Gummiband jenseits ACTION_W → asymptotisch, hart bei MAX gekappt.
  const clampRubber = (delta) => {
    const a = Math.abs(delta);
    if (a <= ACTION_W) return delta;
    const over = a - ACTION_W;
    const damped = ACTION_W + over * (1 - over / (over + 160));
    return Math.sign(delta) * Math.min(damped, MAX);
  };

  // Direktes Setzen von transform/scale (kein Re-Render → 1:1).
  const paint = (dx, withTransition) => {
    dxRef.current = dx;
    const c = contentRef.current;
    if (c) {
      c.style.transition = withTransition ? `transform 300ms ${EASE_SNAP}` : 'none';
      c.style.transform = `translate3d(${dx}px,0,0)`;
    }
    const ic = iconRef.current;
    if (ic) {
      const p = Math.min(1, Math.abs(dx) / ACTION_W);
      // Premium-Pop: Overshoot-Scale + Blur-Clear + Settle aus der Kante in die Mitte.
      ic.style.transition = withTransition
        ? `transform 480ms ${POP}, opacity 260ms ease, filter 300ms ease`
        : 'transform 50ms linear, opacity 50ms linear, filter 50ms linear';
      ic.style.transform = `scale(${0.4 + p * 0.6}) translateX(${(1 - p) * 6 * -sign}px)`;
      ic.style.opacity = String(Math.max(0, p * 1.1 - 0.05));
      ic.style.filter = `blur(${(1 - p) * 4}px)`;
    }
  };

  const snapOpen = useCallback(() => { setOpen(true); paint(sign * ACTION_W, true); haptic(8); }, [sign]);
  const snapClosed = useCallback(() => { setOpen(false); paint(0, true); }, []);

  const onPointerDown = (e) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    tracking.current = true;
    moved.current = false;
    tick.current = false;
    startX.current = e.clientX;
    startY.current = e.clientY;
    lastX.current = e.clientX;
    lastT.current = e.timeStamp;
    vel.current = 0;
    baseRef.current = open ? sign * ACTION_W : 0;
  };

  const onPointerMove = (e) => {
    if (!tracking.current) return;
    const dX = e.clientX - startX.current;
    const dY = e.clientY - startY.current;
    const aX = Math.abs(dX);
    const aY = Math.abs(dY);

    // Axis-Lock: vertikale Geste sofort abgeben (Scroll). Horizontal engagiert
    // responsiver (ab 8px wenn überwiegend horizontal) — die alte 1.5×-Hürde
    // fühlte sich träge an.
    if (!moved.current) {
      if (aY >= 6 && aY > aX) { tracking.current = false; return; }
      if (aX < 8 || aX < aY) return;
      moved.current = true;
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
    }

    const dt = e.timeStamp - lastT.current;
    if (dt > 0) vel.current = vel.current * 0.6 + ((e.clientX - lastX.current) / dt) * 0.4;
    lastX.current = e.clientX;
    lastT.current = e.timeStamp;

    let next = baseRef.current + dX;
    next = sign === -1 ? Math.min(0, clampRubber(next)) : Math.max(0, clampRubber(next));
    paint(next, false);

    if (!tick.current && Math.abs(next) >= TRIGGER) { tick.current = true; haptic(5); }
    else if (tick.current && Math.abs(next) < TRIGGER) { tick.current = false; }
  };

  const onPointerUp = (e) => {
    if (!tracking.current) return;
    tracking.current = false;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch {}
    if (!moved.current) { if (open) snapClosed(); return; }

    const traveled = Math.abs(dxRef.current);
    const v = vel.current * sign; // positiv = in Swipe-Richtung
    // Karte kann NICHT raus — nur auf/zu. Flick in Richtung ODER über Schwelle = offen.
    if (traveled >= TRIGGER || v > FLICK) snapOpen();
    else snapClosed();
  };

  const onPointerCancel = () => { tracking.current = false; if (moved.current) snapClosed(); };

  const onClickCapture = (e) => {
    if (moved.current || open) {
      e.stopPropagation();
      e.preventDefault();
      if (open) snapClosed();
      moved.current = false;
    }
  };

  const doAction = (e) => { e.stopPropagation(); haptic(12); snapClosed(); onAction?.(); };

  return (
    <div
      data-noswipe
      style={{
        position: 'relative',
        borderRadius: radius,
        overflow: 'hidden',
        background: T.surface,
        isolation: 'isolate',
        touchAction: 'pan-y',
      }}
    >
      {/* Action-Zone — KEINE Fläche, nur das Icon (poppt beim Wischen). Bei Ruhe
          opacity:0 → es existiert nichts, was an iOS-Rundecken durchblitzen könnte
          (das war der Bleed-Bug der alten roten inset:0-Fläche). */}
      <div
        aria-hidden={!open}
        onClick={doAction}
        role="button"
        aria-label={label}
        style={{
          position: 'absolute', top: 0, bottom: 0,
          [direction === 'left' ? 'right' : 'left']: 0,
          width: ACTION_W,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexDirection: 'column', gap: 4,
          cursor: open ? 'pointer' : 'default',
          pointerEvents: open ? 'auto' : 'none',
          userSelect: 'none', WebkitTapHighlightColor: 'transparent',
          zIndex: 0,
        }}
      >
        {actionIcon && (
          <div ref={iconRef} style={{ transform: `scale(0.4) translateX(${6 * -sign}px)`, opacity: 0, filter: 'blur(4px)', willChange: 'transform, opacity, filter', display: 'flex' }}>
            <Icon d={actionIcon} size={22} stroke={color} sw={2.4} />
          </div>
        )}
        {label && showLabel && (
          <span style={{
            fontFamily: F.family, fontSize: 11, fontWeight: 700,
            letterSpacing: '0.06em', textTransform: 'uppercase', color,
          }}>{label}</span>
        )}
      </div>

      {/* Sliding Content — transform/transition werden NUR via Ref gesetzt
          (nicht im Style-Prop), sonst würde ein Re-Render den Drag zurücksetzen. */}
      <div
        ref={contentRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        onClickCapture={onClickCapture}
        style={{
          position: 'relative',
          touchAction: 'pan-y',
          background: T.surface,
          borderRadius: radius,
          willChange: 'transform',
          zIndex: 1,
        }}
      >
        {children}
      </div>
    </div>
  );
}

export { Icons as _SwipeIcons };
